import { Component, OnInit, Output, EventEmitter } from '@angular/core';
import { WFMasterResource, WFTaskConfig, WFStepConfig, WFActionConfig, WFRoleConfig, WFSectionConfig } from '../../../workflow/workflow';
import { MatDialog } from '@angular/material/dialog';
import { CdkDragDrop, moveItemInArray, transferArrayItem } from '@angular/cdk/drag-drop';
import { FormType } from 'src/app/common/enumerations/enumerations';
import { Role } from 'src/app/components/catalogs/roles/services/role';
import { WfSignature } from 'src/app/components/workflow/workflow';
import { WorkFlowService } from 'src/app/components/workflow/workflow.service';
import { Tooltip } from 'src/app/common/tooltip';
import { ClEditorComponent } from 'src/app/controls/checklist-components/cl-editor/cl-editor.component';

@Component({
  selector: 'scf-v2-settings-signatures',
  templateUrl: './scf-v2-settings-signatures.component.html',
  styleUrls: ['./scf-v2-settings-signatures.component.scss']
})
export class ScfV2SettingsSignaturesComponent implements OnInit {

  @Output() isLoading = new EventEmitter<boolean>();

  sections: any[] = [
    { id: 2, name: 'Work Authorizations', orientation: 'horizontal' },
    { id: 3, name: 'Work Complete', orientation: 'horizontal' },
    { id: 4, name: 'Operational Approval', orientation: 'vertical' },
    { id: 6, name: 'Amendments', orientation: 'horizontal' }
  ];

  workflow?: WFSectionConfig[];

  constructor(
    private _workflow: WorkFlowService,
    private dialog: MatDialog
  ) { }

  ngOnInit(): void {
    this.loadWFConfiguration();
  }

  loadWFConfiguration() {
    this.isLoading.emit(true);
    this._workflow.getWFSectionsByForm(FormType.SCF).toPromise().then(data => {
      this.workflow = data;
      this.isLoading.emit(false);
    });
  }

  getSteps(id: number) {
    const section = this.workflow?.find(x => x.number == id);
    return section?.wfStepConfigs;
  }

  getTasks(step: WFStepConfig) {
    return step.wfTaskConfigs?.sort((a, b) => (a.order ?? 0) - (b.order ?? 0)) ?? [];
  }

  getRoleIds(task: WFTaskConfig) {
    return task.wfRoleConfigs?.map(r => r.roleID);
  }

  getRoleCodes(task: WFTaskConfig) {
    return task.wfRoleConfigs?.map(r => r.roleCode).join('/');
  }

  getAction(action: number, task: WFTaskConfig) {
    return task.wfActionConfigs?.some(x => x.action == action);
  }

  edit(task: WFTaskConfig) {
    const s = {} as WfSignature;
    s.id = task.id;
    s.name = task.name;
    s.question = task.name2;
    s.question2 = task.name3;
    s.question3 = task.name4;
    const options: any[] = task.options ? JSON.parse(task.options) : [];
    s.options = options;
    s.condition = task.condition;
    s.type = task.type;
    s.visible = true;
    s.required = true;
    s.code = task.code;
    s.enabled = true;
    s.configuration = task.configuration;
    s.approveAction = task.wfActionConfigs?.find(x => x.action == 1) ? true : false;
    s.unapproveAction = task.wfActionConfigs?.find(x => x.action == 3) ? true : false;
    s.disapproveAction = task.wfActionConfigs?.find(x => x.action == 2) ? true : false;
    s.approveActionId = task.wfActionConfigs?.find(x => x.action == 1)?.id;
    s.unapproveActionId = task.wfActionConfigs?.find(x => x.action == 3)?.id;
    s.disapproveActionId = task.wfActionConfigs?.find(x => x.action == 2)?.id;
    s.roles = task.wfRoleConfigs?.map(x => x.roleID);
    s.roleCodes = task.wfRoleConfigs?.map(x => x.roleCode).join('/');
    const editor = this.dialog.open(ClEditorComponent, {
      width: '60vw',
      panelClass: 'draggable-dialog',
      disableClose: true,
      data: {
        s,
        roles: task.wfRoleConfigs?.map(x => x.role),
        formType: 1,
        serialNo: 'NoSerial'
      },
      autoFocus: false
    });
    editor.afterClosed().subscribe((data) => {
      if (data) {
        this.save(task, data.s).then(() => {
          this.saveRoles(task, data.roles, data.originalRoles);
          this.loadWFConfiguration();
        });
      }
    });
  }

  async save(task: WFTaskConfig, s: WfSignature) {
    return new Promise((resolve, reject) => {
      const wfTask: WFTaskConfig = {
        id: s.id,
        name: s.name,
        name2: s.question,
        name3: s.question2,
        name4: s.question3,
        enabled: s.enabled,
        required: s.required,
        condition: s.condition,
        options: JSON.stringify(s.options),
        type: s.type,
        code: s.code,
        configuration: s.configuration,
        wfStepConfigID: task.wfStepConfigID,
      }

      if (s.id != 0) {
        this._workflow.putWFTask(wfTask).toPromise().then(async data => {
          if (data) {
            const section = this.workflow?.find(x => x.wfStepConfigs?.some(s => s.wfTaskConfigs?.some(t => t.id == data.id)));
            const step = section?.wfStepConfigs.find((s => s.wfTaskConfigs?.some(t => t.id == data.id)));
            const taskIdx = step?.wfTaskConfigs?.findIndex(t => t.id == data.id) ?? -1;
            step?.wfTaskConfigs?.splice(taskIdx, 1);
            step?.wfTaskConfigs?.push(data);
            this.saveActions(task, s);
            resolve(data);
          }
        });
      }
      // else {
      //   wfTask.id = 0;
      //   this._workflow.postWFTask(wfTask).subscribe(async data => {
      //     wfTask = data;
      //     s.taskId = wfTask.id;
      //     this.saveActions(s);
      //     this.requestRefresh.emit(this.node);
      //     this.displayInfo(this.node, data);
      //     this.loading = false;
      //     this.editing = false;
      //     resolve(data);
      //   });
      // }
    });
  }

  saveActions(task: WFTaskConfig, s: WfSignature) {
    if (s.approveAction && !s.approveActionId) {
      const action: WFActionConfig = {
        id: 0,
        name: 'Approve',
        label: 'Approve',
        action: 1,
        wfTaskConfigID: task.id
      };
      this.saveAction(s.approveAction, action);
    }
    if (!s.approveAction && s.approveActionId) {
      this.saveAction(s.approveAction ?? false, task.wfActionConfigs?.find(x => x.id == s.approveActionId));
    }

    if (s.unapproveAction && !s.unapproveActionId) {
      const action: WFActionConfig = {
        id: 0,
        name: 'Unapprove',
        label: 'Unapprove',
        action: 3,
        wfTaskConfigID: task.id
      };
      this.saveAction(s.approveAction ?? false, action);
    }
    if (!s.unapproveAction && s.unapproveActionId) {
      this.saveAction(s.unapproveAction ?? false, task.wfActionConfigs?.find(x => x.id == s.unapproveActionId));
    }

    if (s.disapproveAction && !s.disapproveActionId) {
      const action: WFActionConfig = {
        id: 0,
        name: 'Dispprove',
        label: 'Dispprove',
        action: 2,
        wfTaskConfigID: task.id
      };
      this.saveAction(s.disapproveAction, action);
    }
    if (!s.disapproveAction && s.disapproveActionId) {
      this.saveAction(s.disapproveAction ?? false, task.wfActionConfigs?.find(x => x.id == s.disapproveActionId));
    }
  }

  saveAction(checked: boolean, action: any) {

    const id = action.id ? action.id : 0;
    let wfAction: WFActionConfig;

    wfAction = {
      id,
      name: action.name ? action.name : action.key,
      label: action.label ? action.label : action.key,
      action: action.action,
      wfTaskConfigID: action.wfTaskConfigID,
      wfNextStepConfigID: action.next
    };

    if (checked) {
      if (id == 0) {
        // Post
        this._workflow.postWFAction(wfAction).subscribe(data => {
        });
      }
      else {
        // Put
        this._workflow.putWFAction(wfAction).subscribe(data => {
        });
      }
    }
    else {
      // Delete
      this._workflow.delWFAction(wfAction).subscribe(() => {
      });
    }
  }

  saveRoles(task: WFTaskConfig, roles: Role[], roleConfigs: Role[]) {
    return new Promise((resolve, reject) => {
      roles.map(r => {
        if (!roleConfigs.map(x => x.id).includes(r.id)) {
          const wfRoleConfig: WFRoleConfig = {
            id: 0,
            roleID: r.id ?? 0,
            wfTaskConfigID: task.id ?? 0
          };
          this._workflow.postWFRole(wfRoleConfig).subscribe(data => { this.loadWFConfiguration(); });
        }
      });

      roleConfigs.map(r => {
        if (!roles.map(x => x.id).includes(r.id)) {
          const wfRoleConfig = task.wfRoleConfigs?.find(x => x.roleID == r.id);
          if (wfRoleConfig)
            this._workflow.delWFRole(wfRoleConfig).subscribe(data => { this.loadWFConfiguration(); });
        }
      });
    });
  }

  drop(event: CdkDragDrop<WFTaskConfig[]>, section: WFSectionConfig, step: WFStepConfig) {
    if (event.previousContainer === event.container) {
      moveItemInArray(event.container.data, event.previousIndex, event.currentIndex);
      event.container.data.map((x, i) => {
        x.order = i;
      });
      this.saveTasks(event.container.data);
    } else {
      transferArrayItem(
        event.previousContainer.data,
        event.container.data,
        event.previousIndex,
        event.currentIndex,
      );
      event.item.data.wfStepConfigID = event.container.id.replace('draglist-', '');
      event.container.data.map((x, i) => {
        x.order = i;
      });
      this.saveTasks(event.previousContainer.data.concat(event.container.data));
    }
  }

  getConnectedTo(section: WFSectionConfig, step: WFStepConfig) {
    return this.workflow?.find(x => x.id == section.id)?.wfStepConfigs.filter(s => s.id != step.id).map(s => 'draglist-' + s.id) ?? '';
  }

  saveTasks(wfTaskConfigs: WFTaskConfig[]) {
    this._workflow.putWFTasks(wfTaskConfigs).toPromise().then(() => { this.loadWFConfiguration(); });
  }

  getTooltips(task: WFTaskConfig) {
    const tooltips = [] as Tooltip[];
    task.wfActionConfigs?.map(x => {
      const section = this.workflow?.find(w => w.wfStepConfigs?.some(s => s.id == x.wfNextStepConfigID));
      const step = section?.wfStepConfigs.find(s => s.id == x.wfNextStepConfigID);
      if (step) {
        const tooltip: Tooltip = { action: x.action, content: 'GOTO: <b class="blue">' + step?.name + '</b>' };
        tooltips.push(tooltip);
      }
    });
    return tooltips;
  }

  calcWidth(step: WFStepConfig) {
    const tasks = this.getTasks(step);
    if ((tasks?.length ?? 0) > 3) {
      return (80 / (tasks?.length ?? 0)) + '%';
    }
    else {
      return '30%';
    }
  }
}
